import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { useToast } from '@/components/ui/Toast';
import { useBillingEntityConfigs } from '@/features/billing/hooks/useBillingEntityConfig';
import { useGroupJobs, useCreateGroupInvoice } from './hooks/useContactGroups';

const today = () => new Date().toISOString().slice(0, 10);

/**
 * GroupInvoiceModal — issues a single consolidated invoice for a group, billed
 * to the group's primary contact. The user selects the billing entity and,
 * optionally, which member jobs to include (defaults to all eligible jobs).
 */
export function GroupInvoiceModal({
  groupId,
  hasPrimary,
  onClose,
}: {
  groupId: string;
  hasPrimary: boolean;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { data: entities } = useBillingEntityConfigs();
  const { data: jobs } = useGroupJobs(groupId);
  const createInvoice = useCreateGroupInvoice(groupId);

  const [billingEntityId, setBillingEntityId] = useState('');
  const [issueDate, setIssueDate] = useState(today());
  const [invoicePrefix, setInvoicePrefix] = useState('');
  const [selectedJobIds, setSelectedJobIds] = useState<string[]>([]);
  const [error, setError] = useState('');

  // Eligible jobs = not cancelled AND not already invoiced (on a non-void
  // invoice). A job whose invoice was voided reappears here automatically.
  const eligibleJobs = useMemo(
    () => (jobs ?? []).filter((j) => j.status !== 'cancelled' && !j.invoiced),
    [jobs],
  );

  const toggleJob = (id: string) =>
    setSelectedJobIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!billingEntityId) {
      setError('Select a billing entity');
      return;
    }
    createInvoice.mutate(
      {
        billing_entity_id: billingEntityId,
        issue_date: issueDate,
        invoice_prefix: invoicePrefix || undefined,
        // Empty selection = all eligible jobs (server default).
        job_ids: selectedJobIds.length > 0 ? selectedJobIds : undefined,
      },
      {
        onSuccess: (inv) => {
          toast({
            variant: 'success',
            title: `Draft group invoice ${inv.invoice_number} created`,
            description: 'Opening it for review — edit and then send when ready.',
          });
          onClose();
          // Open the newly-created DRAFT invoice in the billing edit screen so
          // the user can review/edit and commit (send) it manually. Use
          // `replace` so closing the draft doesn't land back on the screen the
          // group invoice was started from (e.g. another invoice form).
          navigate(`/billing/${inv.id}`, { replace: true });
        },
        onError: (err: unknown) => {
          const message =
            (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data
              ?.error?.message ?? 'Could not create group invoice';
          setError(message);
        },
      },
    );
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Generate Group Invoice"
      description="Creates a DRAFT invoice billed to the group's primary contact and opens it for review. Nothing is charged until you edit and send it."
      size="lg"
    >
      {!hasPrimary ? (
        <div className="rounded-lg bg-warning-50 p-4 text-sm text-warning-800 dark:bg-warning-900/20 dark:text-warning-300">
          This group has no billing (primary) contact. Set one on the Members tab before issuing a
          group invoice.
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <Select
            label="Billing entity"
            value={billingEntityId}
            onChange={(e) => setBillingEntityId(e.target.value)}
            placeholder="Select a billing entity"
            options={(entities ?? []).map((en) => ({
              value: en.practiceEntityId,
              label: en.billingEntityName || en.practiceEntityCode || en.practiceEntityId,
            }))}
          />
          <div className="grid grid-cols-2 gap-3">
            <Input
              type="date"
              label="Issue date"
              value={issueDate}
              onChange={(e) => setIssueDate(e.target.value)}
            />
            <Input
              label="Invoice prefix (optional)"
              value={invoicePrefix}
              onChange={(e) => setInvoicePrefix(e.target.value)}
              placeholder="Defaults to entity prefix"
            />
          </div>

          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Jobs to bill
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {selectedJobIds.length === 0
                  ? 'All eligible jobs'
                  : `${selectedJobIds.length} selected`}
              </span>
            </div>
            {eligibleJobs.length === 0 ? (
              <p className="rounded-lg border border-border p-3 text-sm text-gray-500 dark:border-border-dark dark:text-gray-400">
                No eligible jobs found. You can still add ad-hoc charges later, or the invoice will
                be empty.
              </p>
            ) : (
              <ul className="max-h-56 space-y-1 overflow-y-auto rounded-lg border border-border p-2 dark:border-border-dark">
                {eligibleJobs.map((j) => (
                  <li key={j.id}>
                    <label className="flex cursor-pointer items-center justify-between rounded px-2 py-1.5 hover:bg-surface-secondary dark:hover:bg-surface-dark-secondary">
                      <span className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectedJobIds.includes(j.id)}
                          onChange={() => toggleJob(j.id)}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm text-gray-900 dark:text-gray-100">
                          {j.job_code ? `${j.job_code} · ` : ''}
                          {j.title}
                          <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">
                            ({j.client_name})
                          </span>
                        </span>
                      </span>
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        ${Number(j.budget_amount ?? 0).toFixed(2)}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Leave all unchecked to bill every eligible job. Already-invoiced jobs are excluded
              automatically.
            </p>
          </div>

          {error && <p className="text-sm text-danger-600">{error}</p>}

          <div className="flex justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={createInvoice.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" loading={createInvoice.isPending}>
              Create Draft &amp; Review
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
